import React from 'react';
import { Link } from 'react-router-dom';
import { Activity, ArrowRight } from 'lucide-react';

/**
 * Public-page Pulse Report £49 instant-order CTA.
 *
 * Lives at the bottom of every public SEO landing — buyers / sellers
 * researching a city can buy a Pulse Investment Report *before* signing up.
 * Routes to /free-valuation which handles the Stripe Hosted Checkout
 * (£49 / ₹4,900 / C$79 NET, Profit-Guard grossed-up). Zero backend
 * additions — uses the existing pulse flow.
 *
 * Props:
 *   role     — buyer | seller | nri | agent  (drives copy)
 *   city     — optional city for ref-param attribution
 *   country  — UK | IN | CA
 */
const PulseReportCTA = ({ role = 'buyer', city = '', country = 'UK', dataTestId = 'public-pulse-cta' }) => {
  const ref = city ? `${role}-${city.toLowerCase().replace(/\s/g, '-')}` : `${role}-public`;
  const cc = (country || 'UK').toUpperCase();
  const sym = cc === 'IN' ? '₹4,900' : cc === 'CA' ? 'C$79' : '£49';
  const cityHook = city ? `for ${city}` : 'for your city';

  const copyByRole = {
    buyer: {
      eyebrow: 'Research before you bid',
      headline: `Get a Pulse Investment Report ${cityHook} for ${sym}`,
      sub: 'Median price, comps, AI commentary and 12-month outlook — emailed in 60 seconds. No signup required.',
      btn: 'Buy Pulse Report',
    },
    seller: {
      eyebrow: 'Price with confidence',
      headline: `Independent Pulse Report ${cityHook} — ${sym}`,
      sub: 'AI-driven comps + median price evidence so you list at the right number. Emailed in 60 seconds.',
      btn: 'Order Pulse Report',
    },
    nri: {
      eyebrow: 'Research from abroad',
      headline: `Independent Pulse Report ${cityHook} — ${sym}`,
      sub: 'Median ₹/sq ft, builder reputation, rental yields and 12-month outlook — emailed before you fly.',
      btn: 'Order Pulse Report',
    },
    agent: {
      eyebrow: 'Win the listing',
      headline: `Pulse Report ${cityHook} — ${sym} — give it to your vendor`,
      sub: 'Hand your next vendor an independent AI report. Built to win listings against Rightmove agents.',
      btn: 'Order for client',
    },
  };
  const c = copyByRole[role] || copyByRole.buyer;

  return (
    <section className="bg-gradient-to-r from-[#FEF8EC] via-white to-[#FEF3EC] border-y border-[#E07A3A]/20" data-testid={dataTestId}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12 sm:py-14">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
          <div className="bg-[#E07A3A]/10 border border-[#E07A3A]/30 p-3 rounded flex-shrink-0">
            <Activity className="h-7 w-7 text-[#E07A3A]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] tracking-[0.3em] text-[#E07A3A] uppercase font-bold mb-1">{c.eyebrow}</p>
            <h2 className="font-['Playfair_Display'] text-2xl sm:text-3xl text-[#0F172A] leading-tight">{c.headline}</h2>
            <p className="text-sm text-slate-600 mt-2 leading-relaxed">{c.sub}</p>
          </div>
          <Link
            to={`/free-valuation?country=${cc}&ref=${ref}`}
            className="inline-flex items-center gap-2 bg-[#E07A3A] hover:bg-[#C4652E] text-white text-sm tracking-[0.2em] font-bold uppercase px-6 py-3.5 transition-colors flex-shrink-0"
            data-testid={`${dataTestId}-btn`}
          >
            {c.btn}
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default PulseReportCTA;
